import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Target, Star, ClipboardList, Eye, ListTodo } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import GoalCards from '../components/my-home/GoalCards';
import ProjectCards from '../components/my-home/ProjectCards';
import FavoriteTabs from '../components/my-home/FavoriteTabs';
import MyIssuesDashboard from '../components/my-home/MyIssuesDashboard';
import PendingReviews from '../components/my-home/PendingReviews';
import TodoSidePanel from '../components/my-home/TodoSidePanel';
import { useMyPendingReviews } from '../hooks/useMyHome';

/* ── 섹션 접기/펼치기 상태를 localStorage에 저장 ── */
const STORAGE_KEY = 'myHome:collapsedSections';

type SectionId = 'goals' | 'favorites' | 'myWork' | 'pendingReview' | 'todo';

function loadCollapsed(): Record<SectionId, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { goals: false, favorites: false, myWork: false, pendingReview: false, todo: false };
}

function saveCollapsed(state: Record<SectionId, boolean>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

/* ── 인사말 헬퍼 ── */
function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return '좋은 아침이에요';
  if (h < 18) return '좋은 오후예요';
  return '좋은 저녁이에요';
}

function formatToday(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const weekday = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
  return `${year}년 ${month}월 ${day}일 (${weekday})`;
}

/* ── 아코디언 섹션 컴포넌트 ── */
function Section({
  id,
  icon: Icon,
  title,
  collapsed,
  onToggle,
  children,
}: {
  id: SectionId;
  icon: React.ElementType;
  title: string;
  collapsed: boolean;
  onToggle: (id: SectionId) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="flex items-center gap-2 w-full px-5 py-4 text-left"
      >
        {collapsed ? (
          <ChevronRight className="size-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="size-4 text-gray-400 shrink-0" />
        )}
        <Icon className="size-4 text-gray-500 shrink-0" />
        <span className="text-sm font-semibold text-gray-900">{title}</span>
      </button>
      {!collapsed && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

/* ── 리뷰 대기 조건부 섹션 (0건이면 전체 숨김) ── */
function PendingReviewSection({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: (id: SectionId) => void;
}) {
  const { data: reviews } = useMyPendingReviews();

  if (!reviews || reviews.length === 0) return null;

  return (
    <Section
      id="pendingReview"
      icon={Eye}
      title={`리뷰 대기 (${reviews.length})`}
      collapsed={collapsed}
      onToggle={onToggle}
    >
      <PendingReviews />
    </Section>
  );
}

/* ── 메인 컴포넌트 ── */
export default function MyHomePage() {
  const user = useAuthStore((s) => s.user);
  const [collapsed, setCollapsed] = useState<Record<SectionId, boolean>>(loadCollapsed);

  useEffect(() => {
    saveCollapsed(collapsed);
  }, [collapsed]);

  const toggle = useCallback((id: SectionId) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return (
    <div className="space-y-5">
      {/* 1. 인사 헤더 (전체 폭) */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {getGreeting()}, {user?.name ?? '사용자'}님 👋
        </h1>
        <p className="text-sm text-gray-500 mt-1">{formatToday()}</p>
      </div>

      {/* 메인 + 사이드 2컬럼 */}
      <div className="flex flex-col lg:flex-row gap-6 items-start">
      {/* ── 좌측 메인 영역 (~70%) ── */}
      <div className="flex-1 min-w-0 space-y-5">
        {/* 2. 목표 · 프로젝트 */}
        <Section
          id="goals"
          icon={Target}
          title="목표 · 프로젝트"
          collapsed={collapsed.goals}
          onToggle={toggle}
        >
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">목표</h4>
              <GoalCards />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">프로젝트</h4>
              <ProjectCards />
            </div>
          </div>
        </Section>

        {/* 3. 즐겨찾기 */}
        <Section
          id="favorites"
          icon={Star}
          title="즐겨찾기"
          collapsed={collapsed.favorites}
          onToggle={toggle}
        >
          <FavoriteTabs />
        </Section>

        {/* 4. 내 작업 */}
        <Section
          id="myWork"
          icon={ClipboardList}
          title="내 작업"
          collapsed={collapsed.myWork}
          onToggle={toggle}
        >
          <MyIssuesDashboard />
        </Section>

        {/* 5. 리뷰 대기 — 0건이면 PendingReviews가 null 반환 → 섹션 숨김 */}
        <PendingReviewSection collapsed={collapsed.pendingReview} onToggle={toggle} />
      </div>

      {/* ── 우측 사이드 패널 (~30%) ── */}
      <div className="w-full lg:w-[30%] lg:min-w-[320px] shrink-0 lg:sticky lg:top-4">
        <Section
          id="todo"
          icon={ListTodo}
          title="To-do"
          collapsed={collapsed.todo}
          onToggle={toggle}
        >
          <TodoSidePanel />
        </Section>
      </div>
      </div>
    </div>
  );
}
